"use client";

import { useEffect, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";
import { useRecentlyViewed, type RecentlyViewedItem } from "@/lib/use-recently-viewed";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";

interface RecentlyViewedProps {
	/** Current product to add to recently viewed + exclude from display */
	currentProduct: RecentlyViewedItem;
}

export function RecentlyViewed({ currentProduct }: RecentlyViewedProps) {
	const { locale, channel } = useParams<{ locale: string; channel: string }>();
	const { items, add, mounted } = useRecentlyViewed(locale, channel);
	const t = useTranslations("recentlyViewed");
	const ploc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;

	// Add current product to recently viewed on mount
	useEffect(() => {
		if (mounted) {
			add({ ...currentProduct, locale, channel });
		}
	}, [mounted, currentProduct, locale, channel, add]);

	// Filter out current product from display
	const displayItems = useMemo(
		() => items.filter((item) => item.slug !== currentProduct.slug),
		[items, currentProduct.slug],
	);

	const priceItems = useMemo(
		() =>
			mounted
				? displayItems
						.filter((i): i is RecentlyViewedItem & { price: number } => i.price !== null)
						.map((i) => ({ id: i.slug, amount: i.price, currency: i.currency }))
				: [],
		[mounted, displayItems],
	);
	const resolvedBySlug = usePricingResolvePresentations(locale, channel, priceItems);

	if (!mounted || displayItems.length === 0) {
		return null;
	}

	return (
		<section className="mt-12 border-t border-border pt-8">
			<h2 className="mb-6 text-lg font-semibold tracking-tight">{t("title")}</h2>
			<div className="scrollbar-thin flex gap-4 overflow-x-auto pb-4">
				{displayItems.map((item) => {
					const pricePres = resolvedBySlug.get(item.slug);
					return (
						<Link
							key={item.slug}
							href={`/${locale}/${channel}/products/${item.slug}`}
							className="group flex w-36 shrink-0 flex-col sm:w-44"
						>
							<div className="aspect-square overflow-hidden rounded-lg bg-secondary">
								{item.image ? (
									<Image
										src={item.image}
										alt={item.name}
										width={176}
										height={176}
										className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
									/>
								) : (
									<div className="flex h-full w-full items-center justify-center text-muted-foreground">
										<span className="text-xs">No image</span>
									</div>
								)}
							</div>
							<p className="mt-2 line-clamp-2 text-sm font-medium group-hover:underline">{item.name}</p>
							{item.reviewAvg !== undefined && item.reviewCount !== undefined && item.reviewCount > 0 && (
								<div className="mt-1 flex items-center gap-1">
									<svg
										className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
										viewBox="0 0 20 20"
										aria-hidden="true"
									>
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
									<span className="text-xs font-medium tabular-nums">{item.reviewAvg.toFixed(1)}</span>
									<span className="text-xs text-muted-foreground">({item.reviewCount})</span>
								</div>
							)}
							{item.price !== null && (
								<p className="mt-1 text-sm text-muted-foreground">
									{pricePres ? (
										<ReferencePrimaryPrice
											presentation={pricePres}
											locale={locale}
											channel={channel}
											size="sm"
											className="text-sm text-muted-foreground"
										/>
									) : (
										formatPrice(item.price, item.currency, ploc)
									)}
								</p>
							)}
						</Link>
					);
				})}
			</div>
		</section>
	);
}
